import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Messaging, getMessaging, getToken, deleteToken, onMessage } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';

const DEVICE_TOKEN_KEY = 'fcm_device_token';

@Injectable({ providedIn: 'root' })
export class FcmService {
  private http = inject(HttpClient);
  private toast = inject(ToastService);

  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;

  private headersFor(accessToken: string): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${accessToken}` });
  }

  /** Requests notification permission and registers this device for push notifications. */
  async register(accessToken: string): Promise<void> {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('firebase-messaging-sw.js');
      const messaging = this.getMessagingInstance();
      const token = await getToken(messaging, {
        vapidKey: environment.firebaseVapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!token || token === localStorage.getItem(DEVICE_TOKEN_KEY)) {
        return;
      }

      await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/api/notifications/device-token/`,
          { token },
          { headers: this.headersFor(accessToken) },
        ),
      );
      localStorage.setItem(DEVICE_TOKEN_KEY, token);

      this.listenForMessages(messaging);
    } catch (error) {
      console.error('Failed to register for push notifications', error);
    }
  }

  /** Unregisters the current device's push token, e.g. on logout. */
  async unregister(accessToken: string): Promise<void> {
    const token = localStorage.getItem(DEVICE_TOKEN_KEY);
    if (!token) {
      return;
    }

    try {
      await firstValueFrom(
        this.http.delete(`${environment.apiUrl}/api/notifications/device-token/`, {
          headers: this.headersFor(accessToken),
          body: { token },
        }),
      );
      await deleteToken(this.getMessagingInstance());
    } catch (error) {
      console.error('Failed to unregister push notification token', error);
    } finally {
      localStorage.removeItem(DEVICE_TOKEN_KEY);
    }
  }

  private listenForMessages(messaging: Messaging): void {
    onMessage(messaging, (payload) => {
      const { title, body } = payload.notification || {};
      this.toast.info(title || 'Notification', body);
    });
  }

  private getMessagingInstance(): Messaging {
    if (!this.app) {
      this.app = initializeApp(environment.firebaseConfig);
    }
    if (!this.messaging) {
      this.messaging = getMessaging(this.app);
    }
    return this.messaging;
  }
}
